import { createStore } from '@xstate/store';
import type { GlobalTask } from '../types';
import type { GlobalTasksContext, GlobalTasksById } from './types';
import { createDebouncedPersistence } from './storePersistence';
import { BOARD_START_DATE, businessDayIndexToDate, addBusinessDays, getTaskDayIndex } from '../lib/dateUtils';

// ============================================
// Global Tasks Store - Singleton
// ============================================

const GLOBAL_TASKS_STORAGE_KEY = 'roadmap_2026_global_tasks';

// ============================================
// Persistence
// ============================================

interface SerializableGlobalTask {
  id: string;
  title: string;
  description?: string | unknown[];
  consequence?: string | unknown[];
  startDate: string;
  estimateMode: string;
  defaultEstimate?: { o: number; m: number; p: number };
  estimates: Record<string, { o: number; m: number; p: number } | null>;
  assigneeIds: string[];
  categoryIds: string[];
  requiredSkills?: string;
  requirements_hard?: Array<{ text: string; description?: string }>;
  requirements_soft?: Array<{ text: string; description?: string }>;
}

function migrateStringToBlocks(val: string | unknown[] | undefined): unknown[] | undefined {
  if (!val) return undefined;
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') return [{ type: 'paragraph', content: [{ type: 'text', text: val }] }];
  return undefined;
}

function loadGlobalTasksFromLocalStorage(): GlobalTask[] {
  try {
    const saved = localStorage.getItem(GLOBAL_TASKS_STORAGE_KEY);
    if (!saved) return [];

    const parsed = JSON.parse(saved) as SerializableGlobalTask[];
    if (!Array.isArray(parsed)) return [];

    return parsed.map((t) => {
      const description = migrateStringToBlocks(t.description);
      const consequence = migrateStringToBlocks(t.consequence);
      return {
        id: t.id,
        title: t.title,
        startDate: t.startDate || businessDayIndexToDate(0, BOARD_START_DATE),
        estimateMode: (t.estimateMode as 'default' | 'individual') || 'individual',
        estimates: t.estimates || {},
        assigneeIds: t.assigneeIds || [],
        categoryIds: t.categoryIds ?? [],
        ...(description && { description }),
        ...(consequence && { consequence }),
        ...(t.defaultEstimate && { defaultEstimate: t.defaultEstimate }),
        ...(t.requiredSkills && { requiredSkills: t.requiredSkills }),
        ...(t.requirements_hard && { requirements_hard: t.requirements_hard }),
        ...(t.requirements_soft && { requirements_soft: t.requirements_soft }),
      };
    });
  } catch {
    return [];
  }
}

// ============================================
// Helpers
// ============================================

function normalizeTasks(tasks: GlobalTask[]): { tasksById: GlobalTasksById; taskIds: string[] } {
  const tasksById: GlobalTasksById = {};
  const taskIds: string[] = [];

  for (const task of tasks) {
    tasksById[task.id] = {
      ...task,
      estimateMode: task.estimateMode || 'individual',
      categoryIds: task.categoryIds ?? [],
    };
    taskIds.push(task.id);
  }

  return { tasksById, taskIds };
}

const initialState = normalizeTasks(loadGlobalTasksFromLocalStorage());

// ============================================
// Store
// ============================================

export const globalTasksStore = createStore({
  context: {
    tasksById: initialState.tasksById,
    taskIds: initialState.taskIds,
  } as GlobalTasksContext,

  on: {
    'task.add': (ctx, event: { task: GlobalTask }) => {
      if (ctx.tasksById[event.task.id]) return ctx;
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.task.id]: event.task,
        },
        taskIds: [...ctx.taskIds, event.task.id],
      };
    },

    'task.delete': (ctx, event: { taskId: string }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      const { [event.taskId]: _, ...remainingTasks } = ctx.tasksById;
      return {
        ...ctx,
        tasksById: remainingTasks,
        taskIds: ctx.taskIds.filter(id => id !== event.taskId),
      };
    },

    'task.updateTitle': (ctx, event: { taskId: string; title: string }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.taskId]: { ...task, title: event.title },
        },
      };
    },

    'task.updateStartDate': (ctx, event: { taskId: string; startDate: string }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.taskId]: { ...task, startDate: event.startDate },
        },
      };
    },

    'task.addAssignee': (ctx, event: { taskId: string; employeeId: string }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      if (task.assigneeIds.includes(event.employeeId)) return ctx;
      if (task.estimateMode === 'default') {
        if (!task.defaultEstimate) return ctx;
      } else {
        if (!task.estimates[event.employeeId]) return ctx;
      }
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.taskId]: {
            ...task,
            assigneeIds: [...task.assigneeIds, event.employeeId],
          },
        },
      };
    },

    'task.removeAssignee': (ctx, event: { taskId: string; employeeId: string }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.taskId]: {
            ...task,
            assigneeIds: task.assigneeIds.filter(id => id !== event.employeeId),
          },
        },
      };
    },

    'task.clearAssignees': (ctx, event: { taskId: string }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.taskId]: { ...task, assigneeIds: [] },
        },
      };
    },

    'task.resize': (ctx, event: { taskId: string; durationDays: number; startDayDelta?: number; boardStartDate?: string }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      const bsd = event.boardStartDate ?? BOARD_START_DATE;
      const days = Math.max(1, Math.round(event.durationDays));
      const newStartDate = event.startDayDelta
        ? addBusinessDays(task.startDate, event.startDayDelta, bsd)
        : task.startDate;
      const newDayIndex = getTaskDayIndex(newStartDate, bsd);
      const safeStartDate = newDayIndex < 0 ? businessDayIndexToDate(0, bsd) : newStartDate;
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.taskId]: {
            ...task,
            startDate: safeStartDate,
            estimateMode: 'default' as const,
            defaultEstimate: { o: days, m: days, p: days },
          },
        },
      };
    },

    'task.updateEstimates': (ctx, event: {
      taskId: string;
      defaultEstimate?: { o: number; m: number; p: number };
      estimates?: Record<string, { o: number; m: number; p: number } | null>;
    }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.taskId]: {
            ...task,
            ...(event.defaultEstimate !== undefined && { defaultEstimate: event.defaultEstimate }),
            ...(event.estimates !== undefined && { estimates: event.estimates }),
          },
        },
      };
    },

    'task.updateCategories': (ctx, event: { taskId: string; categoryIds: string[] }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      return {
        ...ctx,
        tasksById: {
          ...ctx.tasksById,
          [event.taskId]: { ...task, categoryIds: event.categoryIds },
        },
      };
    },

    'task.updateDescription': (ctx, event: { taskId: string; description: unknown[] | undefined }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      const updated = { ...task };
      if (event.description) {
        updated.description = event.description;
      } else {
        delete updated.description;
      }
      return { ...ctx, tasksById: { ...ctx.tasksById, [event.taskId]: updated } };
    },

    'task.updateConsequence': (ctx, event: { taskId: string; consequence: unknown[] | undefined }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      const updated = { ...task };
      if (event.consequence) {
        updated.consequence = event.consequence;
      } else {
        delete updated.consequence;
      }
      return { ...ctx, tasksById: { ...ctx.tasksById, [event.taskId]: updated } };
    },

    'task.updateRequiredSkills': (ctx, event: { taskId: string; requiredSkills: string | undefined }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      const updated = { ...task };
      if (event.requiredSkills) {
        updated.requiredSkills = event.requiredSkills;
      } else {
        delete updated.requiredSkills;
      }
      return { ...ctx, tasksById: { ...ctx.tasksById, [event.taskId]: updated } };
    },

    'task.updateEstimateMode': (ctx, event: { taskId: string; estimateMode: 'default' | 'individual' }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      return { ...ctx, tasksById: { ...ctx.tasksById, [event.taskId]: { ...task, estimateMode: event.estimateMode } } };
    },

    'task.updateDefaultEstimate': (ctx, event: { taskId: string; defaultEstimate: { o: number; m: number; p: number } | undefined }) => {
      const task = ctx.tasksById[event.taskId];
      if (!task) return ctx;
      const updated = { ...task };
      if (event.defaultEstimate) {
        updated.defaultEstimate = event.defaultEstimate;
      } else {
        delete updated.defaultEstimate;
      }
      return { ...ctx, tasksById: { ...ctx.tasksById, [event.taskId]: updated } };
    },

    'tasks.shiftAllDays': (ctx, event: { delta: number; boardStartDate?: string }) => {
      const bsd = event.boardStartDate ?? BOARD_START_DATE;
      const newTasksById = { ...ctx.tasksById };
      for (const taskId of ctx.taskIds) {
        const task = newTasksById[taskId];
        if (task) {
          const currentDayIndex = getTaskDayIndex(task.startDate, bsd);
          const newDayIndex = Math.max(0, currentDayIndex + event.delta);
          newTasksById[taskId] = { ...task, startDate: businessDayIndexToDate(newDayIndex, bsd) };
        }
      }
      return {
        ...ctx,
        tasksById: newTasksById,
      };
    },

    'tasks.shiftDays': (ctx, event: { delta: number; taskIds?: string[]; boardStartDate?: string }) => {
      const bsd = event.boardStartDate ?? BOARD_START_DATE;
      const idsToShift = event.taskIds ?? ctx.taskIds;
      const newTasksById = { ...ctx.tasksById };
      for (const taskId of idsToShift) {
        const task = newTasksById[taskId];
        if (task) {
          const currentDayIndex = getTaskDayIndex(task.startDate, bsd);
          const newDayIndex = Math.max(0, currentDayIndex + event.delta);
          newTasksById[taskId] = { ...task, startDate: businessDayIndexToDate(newDayIndex, bsd) };
        }
      }
      return {
        ...ctx,
        tasksById: newTasksById,
      };
    },

    'tasks.import': (_ctx, event: { tasks: GlobalTask[] }) => {
      const { tasksById, taskIds } = normalizeTasks(event.tasks);
      return { tasksById, taskIds };
    },

    'tasks.reset': (_ctx, event: { tasks: GlobalTask[] }) => {
      const { tasksById, taskIds } = normalizeTasks(event.tasks);
      return { tasksById, taskIds };
    },
  },
});

// ============================================
// Side Effects: Debounced Persistence
// ============================================

const persistence = createDebouncedPersistence<GlobalTasksContext>(
  GLOBAL_TASKS_STORAGE_KEY,
  (ctx) => {
    const tasks: SerializableGlobalTask[] = ctx.taskIds
      .map(id => ctx.tasksById[id])
      .filter(Boolean)
      .map((t) => ({
        id: t.id,
        title: t.title,
        startDate: t.startDate,
        estimateMode: t.estimateMode,
        estimates: t.estimates,
        assigneeIds: t.assigneeIds,
        categoryIds: t.categoryIds,
        ...(Array.isArray(t.description) && t.description.length > 0 && { description: t.description }),
        ...(Array.isArray(t.consequence) && t.consequence.length > 0 && { consequence: t.consequence }),
        ...(t.defaultEstimate && { defaultEstimate: t.defaultEstimate }),
        ...(typeof t.requiredSkills === 'string' && { requiredSkills: t.requiredSkills }),
        ...(t.requirements_hard && { requirements_hard: t.requirements_hard }),
        ...(t.requirements_soft && { requirements_soft: t.requirements_soft }),
      }));
    return JSON.stringify(tasks);
  },
  (ctx) => JSON.stringify(ctx),
);

globalTasksStore.subscribe((snapshot) => {
  persistence.save(snapshot.context);
});

// ============================================
// Helper Functions
// ============================================

export function getGlobalTasksArray(): GlobalTask[] {
  const { tasksById, taskIds } = globalTasksStore.getSnapshot().context;
  return taskIds.map(id => tasksById[id]).filter(Boolean);
}

export function getGlobalTaskById(taskId: string): GlobalTask | undefined {
  return globalTasksStore.getSnapshot().context.tasksById[taskId];
}

export type GlobalTasksStore = typeof globalTasksStore;
